/**
 * @description
 * Centralized backend API service for the MediConnect Patient View.
 * Wraps all FastAPI backend calls with typed responses, Supabase JWT auth injection,
 * and graceful fallback to mock data when the backend is unreachable.
 *
 * Supabase remains the source of truth for:
 *   - appointments
 *   - test results
 *   - messages
 *   - patient profile / vital signs
 *
 * This service covers data served exclusively by the FastAPI backend:
 *   - prescriptions / medications
 *   - medical conditions
 *   - allergies
 *   - surgeries
 *   - medical documents
 *   - care reminders / notifications
 */

import apiClient from '../api/api-client';
import { AxiosError } from 'axios';

// --- Domain types (frontend-canonical shapes) ---

import { Medication } from '../types/medication-types';
import { Condition } from '../components/medical-history/condition-card';
import { MedicalDocument } from '../components/medical-history/document-viewer';
import { CareReminder } from '../components/dashboard/CareReminders';

// Allergy and Surgery share the same structural shape as Condition in this codebase.
export type Allergy = Condition;
export type Surgery = Condition;

// --- Backend response shapes (snake_case from FastAPI) ---

interface BackendPrescription {
  id: string;
  patient_id: string;
  medication_id: string;
  medication_name: string;
  dosage: string;
  frequency: string;
  duration?: string | null;
  prescribed_date: string;
  start_date: string;
  end_date?: string | null;
  status: string;
  notes?: string | null;
  prescribed_by?: string | null;
  created_at: string;
  updated_at: string;
}

interface BackendTreatment {
  id: string;
  name: string;
  type: 'medication' | 'procedure' | 'therapy' | 'lifestyle';
  dosage?: string;
  frequency?: string;
  start_date: string;
  end_date?: string;
  notes?: string;
}

interface BackendDiagnostic {
  id: string;
  name: string;
  date: string;
  result: string;
  attachment_url?: string;
}

interface BackendMedicalRecord {
  id: string;
  record_type: 'condition' | 'allergy' | 'surgery';
  name: string;
  diagnosis_date: string;
  status: 'active' | 'resolved' | 'remission' | 'chronic';
  severity: 'mild' | 'moderate' | 'severe';
  description: string;
  symptoms?: string[];
  treatments?: BackendTreatment[];
  diagnostics?: BackendDiagnostic[];
  location?: string;
}

interface BackendNotification {
  id: string;
  message: string;
  priority: 'high' | 'medium' | 'low' | 'info';
  due_date?: string | null;
  action_link?: string | null;
  action_text?: string | null;
}

interface BackendDocument {
  id: string;
  title: string;
  document_type: 'pdf' | 'image' | 'report' | 'lab' | 'prescription' | 'discharge';
  date: string;
  provider: string;
  url: string;
  thumbnail_url?: string;
  description?: string;
  pages?: number;
}

// --- Response envelope ---

interface ApiListResponse<T> {
  data: T[];
  total?: number;
}

// --- Mock fallback data ---
// These are intentionally minimal; the real data comes from the backend.

const MOCK_MEDICATIONS: Medication[] = [
  {
    id: 'm1-uuid',
    medicationId: 'med-lisinopril',
    patientId: 'mock-patient',
    name: 'Lisinopril',
    dosage: '10mg',
    frequency: 'Once Daily',
    prescribedBy: null,
    prescribedDate: '2023-01-15',
    startDate: '2023-01-15T00:00:00Z',
    endDate: null,
    status: 'active',
    createdAt: '2023-01-15T00:00:00Z',
    updatedAt: '2024-03-10T00:00:00Z',
    notes: 'Take one tablet by mouth every morning.',
  },
  {
    id: 'm2-uuid',
    medicationId: 'med-metformin',
    patientId: 'mock-patient',
    name: 'Metformin',
    dosage: '500mg',
    frequency: 'Twice Daily',
    prescribedBy: null,
    prescribedDate: '2023-01-15',
    startDate: '2023-01-15T00:00:00Z',
    endDate: null,
    status: 'active',
    createdAt: '2023-01-15T00:00:00Z',
    updatedAt: '2024-03-10T00:00:00Z',
    notes: 'Take one tablet by mouth twice daily with meals.',
  },
  {
    id: 'm3-uuid',
    medicationId: 'med-atorvastatin',
    patientId: 'mock-patient',
    name: 'Atorvastatin',
    dosage: '20mg',
    frequency: 'Once Daily',
    prescribedBy: null,
    prescribedDate: '2023-05-20',
    startDate: '2023-05-20T00:00:00Z',
    endDate: null,
    status: 'active',
    createdAt: '2023-05-20T00:00:00Z',
    updatedAt: '2024-02-20T00:00:00Z',
    notes: 'Take one tablet by mouth every evening.',
  },
];

const MOCK_CONDITIONS: Condition[] = [
  {
    id: 'cond-1',
    name: 'Hypertension',
    diagnosisDate: '2022-03-15',
    status: 'active',
    severity: 'moderate',
    description: 'Essential (primary) hypertension with consistently elevated blood pressure readings.',
    symptoms: ['Occasional headaches', 'Dizziness', 'Fatigue'],
    treatments: [
      {
        id: 'treat-1',
        name: 'Lisinopril',
        type: 'medication',
        dosage: '10mg',
        frequency: 'Once daily',
        startDate: '2022-03-20',
        notes: 'Take in the morning with food',
      },
    ],
  },
  {
    id: 'cond-2',
    name: 'Type 2 Diabetes',
    diagnosisDate: '2021-11-10',
    status: 'active',
    severity: 'moderate',
    description: 'Adult-onset diabetes mellitus with insulin resistance.',
    symptoms: ['Increased thirst', 'Frequent urination', 'Fatigue'],
    treatments: [
      {
        id: 'treat-2',
        name: 'Metformin',
        type: 'medication',
        dosage: '500mg',
        frequency: 'Twice daily',
        startDate: '2021-11-15',
      },
    ],
  },
];

const MOCK_ALLERGIES: Allergy[] = [
  {
    id: 'allergy-1',
    name: 'Penicillin Allergy',
    diagnosisDate: '2015-09-10',
    status: 'active',
    severity: 'severe',
    description: 'Severe allergic reaction to penicillin-based antibiotics.',
    symptoms: ['Hives', 'Difficulty breathing', 'Swelling'],
    treatments: [
      {
        id: 'treat-a1',
        name: 'Avoid all penicillin-based antibiotics',
        type: 'lifestyle',
        startDate: '2015-09-10',
      },
    ],
  },
];

const MOCK_SURGERIES: Surgery[] = [
  {
    id: 'surgery-1',
    name: 'Appendectomy',
    diagnosisDate: '2018-07-22',
    status: 'resolved',
    severity: 'moderate',
    description: 'Laparoscopic surgical removal of the appendix due to acute appendicitis.',
    symptoms: ['Right lower quadrant pain', 'Nausea', 'Fever'],
    treatments: [
      {
        id: 'treat-s1',
        name: 'Surgical follow-up',
        type: 'procedure',
        startDate: '2018-08-05',
        endDate: '2018-08-05',
      },
    ],
  },
];

const MOCK_DOCUMENTS: MedicalDocument[] = [
  {
    id: 'doc-1',
    title: 'Complete Blood Count Results',
    type: 'lab',
    date: '2023-02-12',
    provider: 'City General Hospital Lab',
    url: 'https://example.com/cbc-results.pdf',
    description: 'Routine annual blood work.',
  },
];

const MOCK_REMINDERS: CareReminder[] = [
  {
    id: 'r1',
    text: 'Annual wellness visit due',
    priority: 'medium',
    dueDate: '2024-12-31',
    actionText: 'Schedule Now',
    actionLink: '/schedule-appointment',
  },
  {
    id: 'r2',
    text: 'Flu shot recommended',
    priority: 'low',
    actionText: 'Learn More',
    actionLink: '/health-tools',
  },
  {
    id: 'r3',
    text: 'Follow-up required for recent lab results',
    priority: 'high',
    actionText: 'View Results',
    actionLink: '/test-results',
  },
];

// --- Mapping helpers (backend snake_case -> frontend camelCase) ---

function mapPrescriptionToMedication(raw: BackendPrescription, patientId: string): Medication {
  return {
    id: raw.id,
    patientId: raw.patient_id ?? patientId,
    medicationId: raw.medication_id,
    name: raw.medication_name,
    dosage: raw.dosage,
    frequency: raw.frequency,
    duration: raw.duration ?? undefined,
    prescribedDate: raw.prescribed_date,
    prescribedBy: raw.prescribed_by ?? null,
    startDate: raw.start_date,
    endDate: raw.end_date ?? null,
    status: raw.status,
    notes: raw.notes ?? null,
    createdAt: raw.created_at,
    updatedAt: raw.updated_at,
  };
}

function mapTreatment(t: BackendTreatment): Condition['treatments'][number] {
  return {
    id: t.id,
    name: t.name,
    type: t.type,
    dosage: t.dosage,
    frequency: t.frequency,
    startDate: t.start_date,
    endDate: t.end_date,
    notes: t.notes,
  };
}

function mapDiagnostic(d: BackendDiagnostic): NonNullable<Condition['diagnostics']>[number] {
  return {
    id: d.id,
    name: d.name,
    date: d.date,
    result: d.result,
    attachmentUrl: d.attachment_url,
  };
}

function mapMedicalRecord(raw: BackendMedicalRecord): Condition {
  return {
    id: raw.id,
    name: raw.name,
    diagnosisDate: raw.diagnosis_date,
    status: raw.status,
    severity: raw.severity,
    description: raw.description,
    symptoms: raw.symptoms ?? [],
    treatments: (raw.treatments ?? []).map(mapTreatment),
    diagnostics: raw.diagnostics ? raw.diagnostics.map(mapDiagnostic) : undefined,
  };
}

function mapDocument(raw: BackendDocument): MedicalDocument {
  return {
    id: raw.id,
    title: raw.title,
    type: raw.document_type,
    date: raw.date,
    provider: raw.provider,
    url: raw.url,
    thumbnailUrl: raw.thumbnail_url,
    description: raw.description,
    pages: raw.pages,
  };
}

function mapNotification(raw: BackendNotification): CareReminder {
  return {
    id: raw.id,
    text: raw.message,
    priority: raw.priority,
    dueDate: raw.due_date ?? undefined,
    actionLink: raw.action_link ?? undefined,
    actionText: raw.action_text ?? undefined,
  };
}

// --- Utility: detect network / backend errors and decide whether to fall back ---

function isBackendUnavailable(err: unknown): boolean {
  if (err instanceof AxiosError) {
    // Network error (backend not running) or 5xx
    return !err.response || (err.response.status >= 500 && err.response.status < 600);
  }
  return false;
}

// --- Public API service methods ---

/**
 * Fetches prescriptions (medications) for a patient from the FastAPI backend.
 * Falls back to mock data if the backend is unavailable.
 */
export async function getMedications(patientId: string): Promise<Medication[]> {
  try {
    const response = await apiClient.get<ApiListResponse<BackendPrescription> | BackendPrescription[]>(
      '/v1/prescriptions',
      { params: { patient_id: patientId } }
    );

    const raw: BackendPrescription[] = Array.isArray(response.data)
      ? response.data
      : (response.data as ApiListResponse<BackendPrescription>).data;

    return raw.map((p) => mapPrescriptionToMedication(p, patientId));
  } catch (err) {
    if (isBackendUnavailable(err)) {
      if (import.meta.env.PROD) {
        return [];
      }
      console.warn('[backend-api] getMedications: backend unavailable, using mock data.');
      return MOCK_MEDICATIONS.map((m) => ({ ...m, patientId }));
    }
    throw err;
  }
}

/**
 * Fetches medical conditions for a patient.
 * Falls back to mock data if the backend is unavailable.
 */
export async function getMedicalConditions(patientId: string): Promise<Condition[]> {
  try {
    const response = await apiClient.get<ApiListResponse<BackendMedicalRecord> | BackendMedicalRecord[]>(
      '/v1/medical-records',
      { params: { patient_id: patientId, record_type: 'condition' } }
    );

    const raw: BackendMedicalRecord[] = Array.isArray(response.data)
      ? response.data
      : (response.data as ApiListResponse<BackendMedicalRecord>).data;

    return raw.map(mapMedicalRecord);
  } catch (err) {
    if (isBackendUnavailable(err)) {
      if (import.meta.env.PROD) {
        return [];
      }
      console.warn('[backend-api] getMedicalConditions: backend unavailable, using mock data.');
      return MOCK_CONDITIONS;
    }
    throw err;
  }
}

/**
 * Fetches allergy records for a patient.
 * Falls back to mock data if the backend is unavailable.
 */
export async function getAllergies(patientId: string): Promise<Allergy[]> {
  try {
    const response = await apiClient.get<ApiListResponse<BackendMedicalRecord> | BackendMedicalRecord[]>(
      '/v1/medical-records',
      { params: { patient_id: patientId, record_type: 'allergy' } }
    );

    const raw: BackendMedicalRecord[] = Array.isArray(response.data)
      ? response.data
      : (response.data as ApiListResponse<BackendMedicalRecord>).data;

    return raw.map(mapMedicalRecord);
  } catch (err) {
    if (isBackendUnavailable(err)) {
      if (import.meta.env.PROD) {
        return [];
      }
      console.warn('[backend-api] getAllergies: backend unavailable, using mock data.');
      return MOCK_ALLERGIES;
    }
    throw err;
  }
}

/**
 * Fetches surgery records for a patient.
 * Falls back to mock data if the backend is unavailable.
 */
export async function getSurgeries(patientId: string): Promise<Surgery[]> {
  try {
    const response = await apiClient.get<ApiListResponse<BackendMedicalRecord> | BackendMedicalRecord[]>(
      '/v1/medical-records',
      { params: { patient_id: patientId, record_type: 'surgery' } }
    );

    const raw: BackendMedicalRecord[] = Array.isArray(response.data)
      ? response.data
      : (response.data as ApiListResponse<BackendMedicalRecord>).data;

    return raw.map(mapMedicalRecord);
  } catch (err) {
    if (isBackendUnavailable(err)) {
      if (import.meta.env.PROD) {
        return [];
      }
      console.warn('[backend-api] getSurgeries: backend unavailable, using mock data.');
      return MOCK_SURGERIES;
    }
    throw err;
  }
}

/**
 * Fetches medical documents for a patient.
 * Falls back to mock data if the backend is unavailable.
 */
export async function getMedicalDocuments(patientId: string): Promise<MedicalDocument[]> {
  try {
    const response = await apiClient.get<ApiListResponse<BackendDocument> | BackendDocument[]>(
      '/v1/medical-records',
      { params: { patient_id: patientId, record_type: 'document' } }
    );

    const raw: BackendDocument[] = Array.isArray(response.data)
      ? response.data
      : (response.data as ApiListResponse<BackendDocument>).data;

    return raw.map(mapDocument);
  } catch (err) {
    if (isBackendUnavailable(err)) {
      if (import.meta.env.PROD) {
        return [];
      }
      console.warn('[backend-api] getMedicalDocuments: backend unavailable, using mock data.');
      return MOCK_DOCUMENTS;
    }
    throw err;
  }
}

/**
 * Fetches care reminder notifications for a patient.
 * Falls back to mock data if the backend is unavailable.
 */
export async function getCareReminders(patientId: string): Promise<CareReminder[]> {
  try {
    const response = await apiClient.get<ApiListResponse<BackendNotification> | BackendNotification[]>(
      '/v1/notifications',
      { params: { patient_id: patientId } }
    );

    const raw: BackendNotification[] = Array.isArray(response.data)
      ? response.data
      : (response.data as ApiListResponse<BackendNotification>).data;

    return raw.map(mapNotification);
  } catch (err) {
    if (isBackendUnavailable(err)) {
      if (import.meta.env.PROD) {
        return [];
      }
      console.warn('[backend-api] getCareReminders: backend unavailable, using mock data.');
      return MOCK_REMINDERS;
    }
    throw err;
  }
}
